<script lang="ts">
import { useAuthInterpreter, useSignInEmailPassword, useSignUpEmailPassword } from '@nhost/vue';
import { useLocale } from 'vuetify';
import { useClientStore } from '@/store/client';
import { useCompanyStore } from '@/store/company';
import { useSigninStore } from '@/store/signin';
import { gql } from '@apollo/client/core';
import nhost, { apolloClient } from '@/plugins/nhost';

// Lightweight UUID validator to avoid extra runtime dependencies here
const isUuid = (value: string) =>
  typeof value === 'string' &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);

const GET_WHITE_LABEL_LOGIN_DATA = gql`
  query GetWhiteLabelLoginData($domain: String!) {
    general_module_configuration_dataroom(where: { custom_domain: { _eq: $domain } }) {
      logo_url
      primary_color
      secondary_color
    }
  }
`;

const GET_USER_ACCESS_TO_CLIENT = gql`
  query GetUserAccessToClient($domain: String!) {
    general_module_configuration_dataroom(where: { custom_domain: { _eq: $domain } }) {
      custom_domain
      client_id
    }
  }
`;


export default {
  setup() {
    const clientStore = useClientStore();
    const companyStore = useCompanyStore();
    const { t } = useLocale();
    const getTranslation = (path: string) => t(path);
    return {
      getTranslation,
      clientStore,
      companyStore,
    };
  },
  data() {
    return {
      // layout / branding
      loading: true,
      whiteLabelLogin: false,
      logoUrl: '@/assets/logo_cockpit_full_svg.svg',
      logoImageData: null as any,

      // Email step
      email: '',
      emailChecked: false,
      checkingEmail: false,
      isEmailRegistered: false,
      invited: false,
      hasAccess: false,

      // Sign in step (if email is registered)
      password: '',
      signInEmailPassword: undefined as any,
      invalidCredentialsAlert: false,
      visible: false,
      rememberMe: false,

      // Sign up step (if email is not registered)
      firstName: '',
      lastName: '',
      confirmPassword: '',
      passwordMismatchAlert: false,
      emailAlreadyRegisteredAlert: false,
      needsEmailVerification: false,
      signUpEmailPassword: undefined as any,
      signUpVisible: false,
      signingUp: false,
    } as any;
  },
  created() {
    // Always clear current client/company before handling auth
    this.clientStore.clearSelectedClient();
    this.companyStore.clearCompany();

    const refreshToken = this.$route.query.token as string;
    const clientId = this.$route.query.client_id as string;
    const companyId = this.$route.query.company_id as string;
    const origin = this.$route.query.origin as string;
    // Where to land after the token is exchanged. The Portfolio module badges send the user
    // straight to a module (`/app/cockpit/financial`, `/app/company-area`, ...) instead of the
    // dashboard. Only same-app absolute paths are accepted — anything else (`//evil.com`,
    // `https://…`, a protocol-relative URL) would turn this sign-in link into an open redirect.
    const redirect = this.$route.query.redirect as string;
    const safeRedirect = /^\/app(\/[\w\-/]*)?(\?tab=[\w-]+)?$/.test(redirect ?? '') ? redirect : '/app';

    // Remember when the user arrived from the cockpit Portfolio page so the
    // "go back" button can return them there instead of controltower.
    if (origin === 'portfolio') {
      localStorage.setItem('platform_origin', 'cockpit-portfolio');
    }

    // If there is no refresh token, this is a white-label login entry
    if (!refreshToken) {
      localStorage.setItem('platform_origin', 'dataroom');
      this.handleWhiteLabelLogin();
    } else {
      const service = useAuthInterpreter();
      const url = `${import.meta.env.VITE_API_GRAPQHQL_URL}/v1/auth/token`;
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      }).then(response => {
        response.json().then(async session => {
          service.value.send({
            type: 'SESSION_UPDATE',
            data: {
              session,
            },
          });

          clientId && (await this.clientStore.setSelectedClientId(clientId));
          companyId && this.companyStore.setCompany({ id: companyId });

          this.loading = false;
          this.$router.push(safeRedirect);
        });
      });
    }

    // Initialize sign in and sign up functions for the refactored flow
    const { signInEmailPassword } = useSignInEmailPassword();
    this.signInEmailPassword = signInEmailPassword as any;

    const { signUpEmailPassword } = useSignUpEmailPassword();
    this.signUpEmailPassword = signUpEmailPassword as any;
  },
  methods: {
    // ---------- Refactored email-first sign-in / sign-up ----------
    async checkEmail(event: Event) {
      event.preventDefault();

      if (!this.email || !this.email.includes('@')) {
        return;
      }

      this.checkingEmail = true;

      try {
        const { data } = await apolloClient.mutate({
          mutation: gql`
            mutation CheckSignInEmail($email: String!, $domain: String!) {
              CheckSignInEmail(payload: { email: $email, domain: $domain }) {
                available
                invited
                hasAccess
              }
            }
          `,
          variables: { email: this.email, domain: window.location.hostname },
        });

        const available = data?.CheckSignInEmail?.available;
        const invited = data?.CheckSignInEmail?.invited;
        const hasAccess = data?.CheckSignInEmail?.hasAccess;

        // If email is NOT available, it means user exists -> show sign-in
        // If email IS available, it means user doesn't exist -> show sign-up
        this.isEmailRegistered = !available;
        this.invited = !!invited;
        this.hasAccess = !!hasAccess;
        this.emailChecked = true;
      } catch (err) {
        console.error('Error checking email:', err);
        this.isEmailRegistered = false;
        this.invited = false;
        this.hasAccess = false;
        this.emailChecked = true;
      }

      this.checkingEmail = false;
    },

    async handleSignIn(event: Event) {
      event.preventDefault();
      this.invalidCredentialsAlert = false;

      const { isSuccess } = await this.signInEmailPassword(this.email, this.password);

      if (isSuccess) {
        // Get the whiteLabelLogin client_id first
        const result = await apolloClient.query({
          query: GET_USER_ACCESS_TO_CLIENT,
          variables: {
            domain: window.location.hostname,
          },
        });
        const whiteLabelClientId = result.data.general_module_configuration_dataroom[0].client_id;

        // TECH-2675 — signing in no longer accepts anything. A `general.user_client_invite` used to
        // be accepted right here (making the person a member of the company, the "viewer dataroom"
        // shape); an outsider is now invited as an investor and accepts on /investor-invite/:token,
        // which creates their own account. The route guard decides what they may open from here.
        await this.clientStore.setSelectedClientId(whiteLabelClientId);
        this.$router.push('/app');
      } else {
        this.invalidCredentialsAlert = true;
      }
    },

    async handleSignUp(event: Event) {
      event.preventDefault();

      if (this.password !== this.confirmPassword) {
        this.passwordMismatchAlert = true;
        return;
      }

      this.invalidCredentialsAlert = false;
      this.passwordMismatchAlert = false;
      this.emailAlreadyRegisteredAlert = false;
      this.signingUp = true;

      try {
        const options = {
          displayName: `${this.firstName} ${this.lastName}`,
          // TECH-2672 — `user`, not `user-dataroom`: the allowed roles default to
          // `user-dataroom,investor` (AUTH_USER_DEFAULT_ALLOWED_ROLES), so an account whose default
          // role is `user-dataroom` has no `user` role at all and cannot use the Cockpit — which the
          // same person reaches through Portfolio. The dataroom app keeps querying as
          // `user-dataroom` regardless (see plugins/nhost.ts).
          defaultRole: 'user',
          locale: 'pt',
          metadata: {
            firstName: this.firstName,
            lastName: this.lastName,
          },
        };

        const { isSuccess, needsEmailVerification, error } = await this.signUpEmailPassword(
          this.email,
          this.password,
          options,
        );

        if (isSuccess) {
          if (needsEmailVerification) {
            this.needsEmailVerification = true;
            this.signingUp = false;
          } else {
            // TECH-2675 — nothing to accept on sign-up either (see handleSignIn above). Land the new
            // account on the domain's client and let the route guard decide what it may open.
            const result = await apolloClient.query({
              query: GET_USER_ACCESS_TO_CLIENT,
              variables: {
                domain: window.location.hostname,
              },
            });
            const whiteLabelClientId = result.data.general_module_configuration_dataroom[0].client_id;
            await this.clientStore.setSelectedClientId(whiteLabelClientId);
            this.$router.push('/app');
          }
        } else if (
          error &&
          (error.message?.includes('already in use') ||
            error.status === 409 ||
            error.error === 'email-already-in-use')
        ) {
          this.emailAlreadyRegisteredAlert = true;
          this.signingUp = false;
        } else {
          this.invalidCredentialsAlert = true;
          this.signingUp = false;
        }
      } catch (err) {
        console.error('Error during sign up:', err);
        this.invalidCredentialsAlert = true;
        this.signingUp = false;
      }
    },

    resetFlow() {
      this.emailChecked = false;
      this.isEmailRegistered = false;
      this.email = '';
      this.password = '';
      this.firstName = '';
      this.lastName = '';
      this.confirmPassword = '';
      this.invalidCredentialsAlert = false;
      this.passwordMismatchAlert = false;
      this.emailAlreadyRegisteredAlert = false;
      this.needsEmailVerification = false;
      this.signingUp = false;
    },

    // ---------- White-label (custom_domain) login flow ----------
    async handleRedirectToAppWhiteLabelLogin() {
      // before redirect, check if user has access to the client of given domain
      const result = await this.$apollo.query({
        query: GET_USER_ACCESS_TO_CLIENT,
        variables: {
          domain: window.location.hostname,
        },
      });
      if (result.data.general_module_configuration_dataroom.length > 0) {
        await this.clientStore.setSelectedClientId(
          result.data.general_module_configuration_dataroom[0].client_id,
        );
        this.$router.push('/app');
      } else {
        // TODO: show error message
        console.error('User does not have access to the client');
      }
    },

    async handleWhiteLabelLogin() {
      this.whiteLabelLogin = true;

      // Initialize sign-in functionality for white label login
      const { signInEmailPassword, user } = useSignInEmailPassword();

      if (user.value?.id) {
        this.handleRedirectToAppWhiteLabelLogin();
        return;
      }

      this.signInEmailPassword = signInEmailPassword as any;

      const store = useSigninStore();
      if (store.getRememberMe) {
        this.email = store.getRememberMe.email;
      }

      this.loading = false;
    },

    async handleSubmit(event: Event) {
      event.preventDefault();

      this.invalidCredentialsAlert = false;

      const { isSuccess, needsEmailVerification, user } = await this.signInEmailPassword(
        this.email,
        this.password,
      );
      this.needsEmailVerification = needsEmailVerification;

      if (isSuccess) {
        if (this.rememberMe) {
          const store = useSigninStore();
          store.setRememberMe({ email: this.email });
        }

        if (user.defaultRole === 'user' || user.defaultRole === 'investor') {
          this.handleRedirectToAppWhiteLabelLogin();
        } else {
          this.$router.push('/app');
        }
      } else {
        this.invalidCredentialsAlert = true;
      }
    },

    async handleForgotPassword(event: Event) {
      event.preventDefault();
      this.$router.push('/forgot-password');
    },

    async handleFirstAccess(event: Event) {
      event.preventDefault();
      this.$router.push('/forgot-password');
    },

    async imageToBase64(url: string): Promise<string> {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous'; // Important for CORS

        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;

            const ctx = canvas.getContext('2d');
            if (!ctx) {
              reject(new Error('Could not get canvas context'));
              return;
            }

            ctx.drawImage(img, 0, 0);
            const base64 = canvas.toDataURL('image/png');
            resolve(base64);
          } catch (error) {
            reject(error);
          }
        };

        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };

        img.src = url;
      });
    },

    async preloadImage(uuid: string, imageDataKey: string) {
      try {
        // 1. Try to load from localStorage
        const cached = localStorage.getItem(`logoBase64_${uuid}`);
        if (cached && cached !== 'null') {
          (this as any)[imageDataKey] = cached;
          return;
        }

        // 2. Fetch presigned URL and convert to base64
        const publicUrl = await nhost.storage.getPresignedUrl({ fileId: uuid });
        if (!publicUrl.presignedUrl) return;
        let url = publicUrl.presignedUrl.url;
        if (url && !/^https?:\/\//.test(url)) url = 'http://' + url;
        const base64Data = await this.imageToBase64(url);

        localStorage.setItem(`logoBase64_${uuid}`, base64Data);
        (this as any)[imageDataKey] = base64Data;
      } catch (error) {
        console.error('Error preloading image:', error);
      }
    },

    async getLogoUrl(logo_url: string) {
      if (!logo_url) return '@/assets/logo_cockpit_full_svg.svg';
      if (!isUuid(logo_url)) {
        return logo_url;
      }

      await this.preloadImage(logo_url, 'logoImageData');
      return this.logoImageData;
    },
  },
  watch: {
    selectedClient: {
      immediate: true,
      handler(newVal) {
        if (newVal?.id) {
          this.handleWhiteLabelLogin();
        }
      },
    },
  },
  apollo: {
    whiteLabelLoginData: {
      query: GET_WHITE_LABEL_LOGIN_DATA,
      variables: {
        domain: window.location.hostname,
      },
      async update(data) {
        if (data.general_module_configuration_dataroom[0]?.logo_url) {
          this.logoUrl = await this.getLogoUrl(
            data.general_module_configuration_dataroom[0].logo_url,
          );
          this.clientStore.setSelectedClientId(
            data.general_module_configuration_dataroom[0].client_id,
          );
          this.loading = false;
        }
        return data.general_module_configuration_dataroom[0];
      },
    },
  },
};
</script>

<template>
  <div class="sign-in-container">
    <div class="sign-in pa-10 d-flex" v-if="!loading">
      <!-- White-label login: refactored email-first sign-in / sign-up -->
      <div class="w-75" v-if="whiteLabelLogin">
        <div class="d-flex justify-center">
          <v-img eager class="mb-10 brand-logo" :src="logoUrl" />
        </div>

        <!-- Email verification step -->
        <form v-if="!emailChecked" @submit="checkEmail">
          <v-text-field
            variant="outlined"
            v-model="email"
            type="email"
            :label="getTranslation('$vuetify.auth.sign-in.email.label') || 'Email'"
            density="compact"
            class="input-compact"
            :disabled="checkingEmail"
            required
          />
          <v-btn
            min-width="100%"
            color="primary"
            class="text-none"
            type="submit"
            :loading="checkingEmail"
            :disabled="checkingEmail || !email || !email.includes('@')"
          >
            {{ getTranslation('$vuetify.auth.sign-in.continue-btn.text') || 'Continue' }}
          </v-btn>
        </form>

        <!-- Sign in form (if email is registered and (has access OR is invited)) -->
        <form
          v-else-if="emailChecked && isEmailRegistered && (hasAccess || invited) && !needsEmailVerification"
          @submit="handleSignIn"
        >
          <div class="d-flex align-center mb-4">
            <v-btn icon variant="text" size="small" @click="resetFlow">
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <span class="ml-2">{{ email }}</span>
          </div>

          <v-text-field
            :append-inner-icon="visible ? 'mdi-eye-off' : 'mdi-eye'"
            :type="visible ? 'text' : 'password'"
            variant="outlined"
            v-model="password"
            @click:append-inner="visible = !visible"
            autocomplete="current-password"
            :label="getTranslation('$vuetify.auth.sign-in.password.label') || 'Password'"
            density="compact"
            class="input-compact"
            required
          />

          <v-alert
            v-model="invalidCredentialsAlert"
            type="error"
            variant="tonal"
            color="danger"
            border="start"
            closable
            :text="getTranslation('$vuetify.auth.sign-in.wrong-credentials.text')"
          />

          <v-btn min-width="100%" color="primary" class="text-none" type="submit">
            {{ getTranslation('$vuetify.auth.sign-in.login-btn.text') || 'Sign In' }}
          </v-btn>

          <div class="d-flex justify-end mt-2">
            <span class="forgot-password-text" @click="$router.push('/forgot-password')">
              {{ getTranslation('$vuetify.auth.sign-in.forgot-password-btn.text') || 'Forgot password?' }}
            </span>
          </div>
        </form>

        <!-- Sign up form (if email is not registered AND user is invited) -->
        <div v-else-if="emailChecked && !isEmailRegistered && invited">
          <p v-if="needsEmailVerification">
            {{ getTranslation('$vuetify.auth.sign-up.needsEmailVerification.text') }}
          </p>

          <form v-else @submit="handleSignUp">
            <div class="d-flex align-center mb-4">
              <v-btn icon variant="text" size="small" @click="resetFlow" :disabled="signingUp">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <span class="ml-2">{{ email }}</span>
            </div>

            <v-text-field
              variant="outlined"
              v-model="firstName"
              type="text"
              :label="getTranslation('$vuetify.auth.sign-up.first-name.label')"
              density="compact"
              class="input-compact"
              :disabled="signingUp"
            />
            <v-text-field
              variant="outlined"
              v-model="lastName"
              type="text"
              :label="getTranslation('$vuetify.auth.sign-up.last-name.label')"
              density="compact"
              class="input-compact"
              :disabled="signingUp"
            />
            <v-text-field
              :append-inner-icon="signUpVisible ? 'mdi-eye-off' : 'mdi-eye'"
              :type="signUpVisible ? 'text' : 'password'"
              variant="outlined"
              v-model="password"
              @click:append-inner="signUpVisible = !signUpVisible"
              autocomplete="new-password"
              :label="getTranslation('$vuetify.auth.sign-up.password.label')"
              density="compact"
              class="input-compact"
              :disabled="signingUp"
            />
            <v-text-field
              :append-inner-icon="signUpVisible ? 'mdi-eye-off' : 'mdi-eye'"
              :type="signUpVisible ? 'text' : 'password'"
              variant="outlined"
              v-model="confirmPassword"
              @click:append-inner="signUpVisible = !signUpVisible"
              autocomplete="new-password"
              :label="getTranslation('$vuetify.auth.sign-up.confirm-password.label')"
              density="compact"
              class="input-compact"
              :disabled="signingUp"
            />

            <v-alert
              v-model="invalidCredentialsAlert"
              type="error"
              variant="tonal"
              color="danger"
              border="start"
              closable
              :text="getTranslation('$vuetify.auth.sign-in.wrong-credentials.text')"
            />
            <v-alert
              v-model="passwordMismatchAlert"
              type="error"
              variant="tonal"
              color="danger"
              border="start"
              closable
              :text="getTranslation('$vuetify.auth.sign-up.password-mismatch-alert.text')"
            />
            <v-alert
              v-model="emailAlreadyRegisteredAlert"
              type="error"
              variant="tonal"
              color="danger"
              border="start"
              closable
              class="alert-compact"
            >
              <span class="error-msg-small">
                {{ getTranslation('$vuetify.auth.sign-up.email-already-registered.text') }}.
                {{ getTranslation('$vuetify.auth.sign-up.email-already-registered.prefix') }}
                <span class="sign-in-link" @click="emailChecked = true; emailAlreadyRegisteredAlert = false">
                  {{ getTranslation('$vuetify.auth.sign-up.email-already-registered.link') }}
                </span>
              </span>
            </v-alert>

            <v-btn
              min-width="100%"
              color="primary"
              class="text-none"
              type="submit"
              :loading="signingUp"
              :disabled="signingUp"
            >
              {{ getTranslation('$vuetify.auth.sign-up.sign-up-btn.text') }}
            </v-btn>
          </form>
        </div>

        <!-- Informative message: user does not exist and is not invited -->
        <div v-else-if="emailChecked && !isEmailRegistered && !invited">
          <v-alert
            type="info"
            variant="tonal"
            color="primary"
            border="start"
          >
            {{
              getTranslation('$vuetify.auth.sign-up.not-invited.text') ||
                'Este e-mail não está cadastrado nem convidado para acessar esta plataforma. Entre em contato com o administrador.'
            }}
          </v-alert>
        </div>

        <!-- Informative message: user exists but does not have access to this platform and is not invited -->
        <div v-else-if="emailChecked && isEmailRegistered && !hasAccess && !invited">
          <v-alert
            type="info"
            variant="tonal"
            color="primary"
            border="start"
          >
            {{
              getTranslation('$vuetify.auth.sign-in.no-access.text') ||
                'Você não tem acesso a esta plataforma. Entre em contato com o administrador.'
            }}
          </v-alert>
        </div>
      </div>

      <!-- Non white-label login (fallback: classic email + password) -->
      <div class="w-75" v-else>
        <div class="d-flex justify-center">
          <v-img eager class="mb-10 brand-logo" :src="logoUrl" />
        </div>

        <p v-if="needsEmailVerification">
          {{ getTranslation('$vuetify.auth.sign-in.needsEmailVerification.text') }}
        </p>

        <v-form @submit="handleSubmit" v-else>
          <v-text-field
            variant="outlined"
            v-model="email"
            type="email"
            :label="getTranslation('$vuetify.auth.sign-in.email.label')"
          />
          <br />
          <v-text-field
            :append-inner-icon="visible ? 'mdi-eye-off' : 'mdi-eye'"
            :type="visible ? 'text' : 'password'"
            variant="outlined"
            v-model="password"
            @click:append-inner="visible = !visible"
            :label="getTranslation('$vuetify.auth.sign-in.password.label')"
          />
          <br />
          <v-btn min-width="100%" color="primary" class="text-white" type="submit">
            <span class="text-white">
              {{ getTranslation('$vuetify.auth.sign-in.login-btn.text') }}
            </span>
          </v-btn>
          <v-alert
            v-model="invalidCredentialsAlert"
            type="error"
            variant="tonal"
            color="danger"
            border="start"
            closable
            :text="getTranslation('$vuetify.auth.sign-in.wrong-credentials.text')"
          />
          <div class="d-flex flex-row justify-space-between mt-5">
            <v-checkbox
              v-model="rememberMe"
              :label="getTranslation('$vuetify.auth.sign-in.remember-me-checkbox.text')"
            ></v-checkbox>
            <p class="ma-4 forgot-password-text" @click="handleForgotPassword">
              {{ getTranslation('$vuetify.auth.sign-in.forgot-password-btn.text') }}
            </p>
            <p class="ma-4 first-access-text" @click="handleFirstAccess">
              {{ getTranslation('$vuetify.auth.sign-in.first-access-btn.text') }}
            </p>
          </div>
        </v-form>
      </div>
    </div>

    <div class="w-75" v-else>
      <div class="d-flex justify-center">
        <v-progress-circular indeterminate color="primary" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.sign-in-container {
  width: 100%;
  min-height: 100%;
}

.sign-in {
  justify-content: center;
  align-items: center;
  text-align: center;
  min-height: fit-content;
  padding-bottom: 2rem;
}

.forgot-password-text {
  color: rgb(var(--v-theme-primary));
  cursor: pointer;
}

.first-access-text {
  color: rgb(var(--v-theme-primary));
  cursor: pointer;
}

.input-compact {
  margin-bottom: 0.5rem !important;
  font-size: 0.95rem !important;
}

.sign-in-link {
  text-decoration: underline;
  cursor: pointer;
}

.alert-compact {
  min-width: 0 !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
  padding-left: 12px !important;
  padding-right: 12px !important;
}

.brand-logo {
  max-width: 500px;         /* fixed logo box */
  max-height: 300px;
  object-fit: contain;  /* keep aspect ratio inside box */
}
</style>
